import moment from 'moment';

import { DisengagementCriteria } from '../constants/disengagementFilters';
import { toJsDate } from '../../utils/date';
import { UserCategory } from '../../Model/User';
import { allIn } from '../../utils/userCategory';

const DISENGAGEMENT_OUTREACH_COOLDOWN_DAYS = 2;

const DEFAULT_DISENGAGEMENT_THRESHOLDS = {
  missedWorkoutDays: 14,
  missedCheckInDays: 14,
  chatSilenceWeekdays: 7,
};

const readPositiveNumber = (value, fallback) => (
  typeof value === 'number' && value > 0 ? value : fallback
);

/**
 * Pure parser: turns an arbitrary PostHog flag payload into a validated
 * thresholds object, falling back to defaults per-field.
 */
const parseThresholds = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { ...DEFAULT_DISENGAGEMENT_THRESHOLDS };
  }
  return {
    missedWorkoutDays: readPositiveNumber(
      payload.missedWorkoutDays,
      DEFAULT_DISENGAGEMENT_THRESHOLDS.missedWorkoutDays,
    ),
    missedCheckInDays: readPositiveNumber(
      payload.missedCheckInDays,
      DEFAULT_DISENGAGEMENT_THRESHOLDS.missedCheckInDays,
    ),
    chatSilenceWeekdays: readPositiveNumber(
      payload.chatSilenceWeekdays,
      DEFAULT_DISENGAGEMENT_THRESHOLDS.chatSilenceWeekdays,
    ),
  };
};

/**
 * Counts weekdays (Mon-Fri) between a past date and now.
 * Mirrors the server implementation in
 * functions-22/src/activityFeed/utils/send-disengaged-client-notification.ts.
 */
const weekdaysSince = (date) => {
  if (!date) return Infinity;
  const start = moment(date);
  const end = moment();
  let weekdays = 0;
  const cursor = start.clone();
  while (cursor.isBefore(end, 'day')) {
    cursor.add(1, 'day');
    const day = cursor.day();
    if (day !== 0 && day !== 6) {
      weekdays += 1;
    }
  }
  return weekdays;
};

/**
 * Returns true when a user is paused from workouts (their
 * workoutNotificationsResumeDate is in the future).
 */
const isWorkoutPaused = (userDoc) => {
  const resumeDate = userDoc.workoutNotificationsResumeDate;
  if (!resumeDate) return false;
  return moment(resumeDate).isAfter(moment());
};

/**
 * Returns true when a user has ever been assigned a workout program.
 */
const hasEverBeenAssignedWorkout = (userDoc) => !!userDoc.lastWorkoutAssignedDate;

/**
 * Whether a client is excluded from disengagement because of their subscription
 * cancellation state: the end date has passed, or they deliberately scheduled a
 * future cancellation. `cancelAt` is unix seconds and is compared against now;
 * `canceledAt` is a Firestore Timestamp and is only tested for presence.
 *
 * Mirrors the server's shouldSkipForCancellation in
 * functions-22/src/activityFeed/utils/send-disengaged-client-notification.ts.
 */
const shouldSkipForCancellation = (userDoc, nowUnix = moment.utc().unix()) => {
  const { cancelAt, canceledAt } = userDoc;
  if (!cancelAt) return false;
  return cancelAt <= nowUnix || !!canceledAt;
};

/**
 * The disengagement eligibility gate: excludes CHALLENGES and AI_FRIEND clients, clients whose
 * subscription ended or who scheduled a future cancellation, clients with an active broadcast
 * silence, and clients reached by outreach inside the cooldown. Kept in one place so the modal's
 * counts, its tag matcher and any future caller stay in step.
 *
 * Mirrors isDisengagementEligible in
 * functions-22/src/in-app-messaging/utils/disengagedRecipients.ts.
 *
 * Both dates are normalised through toJsDate: model getters hand back Dates while raw
 * feed/Firestore data hands back Timestamps, and comparing an un-unwrapped Timestamp against a
 * Date silently yields false (i.e. never gates).
 */
const isDisengagementEligible = (userDoc, productCategories = new Map(), coachId, now = moment.utc()) => {
  // Excluded only when EVERY product the client holds is a challenge or AI Friend — a
  // live 1:1 alongside a challenge is real coaching and stays reachable.
  if (allIn(productCategories, userDoc, coachId, [UserCategory.CHALLENGES, UserCategory.AI_FRIEND])) return false;

  if (shouldSkipForCancellation(userDoc, now.unix())) return false;

  const silencedUntil = toJsDate(userDoc.disengagementBroadcastSilencedUntil);
  if (silencedUntil && moment(silencedUntil).isAfter(now)) return false;

  const reachedAt = toJsDate(userDoc.disengagementOutreachReachedAt);
  const cooldownStart = now.clone().subtract(DISENGAGEMENT_OUTREACH_COOLDOWN_DAYS, 'days');
  if (reachedAt && moment(reachedAt).isAfter(cooldownStart)) return false;

  return true;
};

/**
 * Returns true when a user currently matches MISSED_WORKOUT.
 * Gated on workout notifications not paused AND workouts ever assigned.
 *
 * When the client has never completed a workout, staleness is measured from the
 * assignment date so a recently assigned client is not flagged before
 * missedWorkoutDays has elapsed. Mirrors the server's evaluateDisengagement in
 * functions-22/src/activityFeed/utils/send-disengaged-client-notification.ts.
 *
 * Both baseline fields are normalised through toJsDate: model getters hand back
 * Dates, while raw feed/Firestore data hands back Timestamps, and moment() turns
 * an un-unwrapped Timestamp into an Invalid date (NaN days, never flagged).
 */
const matchesMissedWorkout = (userDoc, thresholds = DEFAULT_DISENGAGEMENT_THRESHOLDS) => {
  if (isWorkoutPaused(userDoc)) return false;
  if (!hasEverBeenAssignedWorkout(userDoc)) return false;
  const baseline = toJsDate(userDoc.lastWorkoutCompletedAt) ?? toJsDate(userDoc.lastWorkoutAssignedDate);
  const days = baseline ? moment().diff(moment(baseline), 'days') : Infinity;
  return days >= thresholds.missedWorkoutDays;
};

/**
 * Returns true when a user currently matches MISSED_CHECK_IN.
 * Gated on the CHECK_IN_NOTIFICATION feature being enabled on the product.
 */
const matchesMissedCheckIn = (userDoc, { checkInEnabled }, thresholds = DEFAULT_DISENGAGEMENT_THRESHOLDS) => {
  if (!checkInEnabled) return false;
  const last = userDoc.lastCheckIn;
  const days = last ? moment().diff(moment(last), 'days') : Infinity;
  return days >= thresholds.missedCheckInDays;
};

/**
 * Returns true when a user currently matches CHAT_SILENCE.
 * The CHAT feature gate is enforced upstream by the modal's pre-filter.
 */
const matchesChatSilence = (userDoc, thresholds = DEFAULT_DISENGAGEMENT_THRESHOLDS) => {
  const weekdays = weekdaysSince(userDoc.lastMessageAt);
  return weekdays >= thresholds.chatSilenceWeekdays;
};

/**
 * Whether a client belongs in the "no chat in N business days" broadcast segment: no 1:1 chat
 * from either side, and they have been a client long enough for that silence to mean something.
 * A policy decision, not a fact — it fails closed at every unknown.
 *
 * Mirrors the server's matchesChatSilenceSegment in
 * functions-22/src/in-app-messaging/utils/chatSilence.ts. Both sides must change together, or
 * the broadcast preview and the send disagree.
 *
 * @param {User} userDoc - A User model scoped to this coach; raw Firestore docs have no
 *   `lastCoachMessageAt` root twin and would silently lose the coach-recency branch.
 * @param {number} businessDays - Weekdays of silence required, applied to both sides.
 * @return {boolean} Whether the client belongs in the segment.
 */
const matchesChatSilenceSegment = (userDoc, businessDays) => {
  if (userDoc.isFutureStartForAllProducts) return false;

  const lastCoachMessageAt = toJsDate(userDoc.lastCoachMessageAt);
  if (lastCoachMessageAt && weekdaysSince(lastCoachMessageAt) < businessDays) return false;

  const lastMessageAt = toJsDate(userDoc.lastMessageAt);
  if (lastMessageAt) return weekdaysSince(lastMessageAt) >= businessDays;

  const baseline = toJsDate(userDoc.serviceStartAt) ?? toJsDate(userDoc.createdAt);
  if (!baseline) return false;
  return weekdaysSince(baseline) >= businessDays;
};

/**
 * Evaluates all disengagement criteria for a user.
 * @returns {DisengagementCriteria[]} matching criteria
 */
const evaluateDisengagement = (userDoc, featureFlags, thresholds = DEFAULT_DISENGAGEMENT_THRESHOLDS) => {
  const matches = [];
  if (matchesMissedCheckIn(userDoc, featureFlags, thresholds)) {
    matches.push(DisengagementCriteria.MISSED_CHECK_IN);
  }
  if (matchesMissedWorkout(userDoc, thresholds)) {
    matches.push(DisengagementCriteria.MISSED_WORKOUT);
  }
  if (matchesChatSilence(userDoc, thresholds)) {
    matches.push(DisengagementCriteria.CHAT_SILENCE);
  }
  return matches;
};

/**
 * Single "is this client disengaged" predicate, mirroring the server's
 * asymmetric 2-signal model: workout is the anchor; check-in is a second
 * required signal only when the feature is enabled.
 */
const isClientDisengaged = (userDoc, { checkInEnabled }, thresholds = DEFAULT_DISENGAGEMENT_THRESHOLDS) => {
  if (!matchesMissedWorkout(userDoc, thresholds)) return false; // handles paused / never-assigned / not-missed
  if (!checkInEnabled) return true; // workout-only fallback
  return matchesMissedCheckIn(userDoc, { checkInEnabled }, thresholds);
};

export {
  DEFAULT_DISENGAGEMENT_THRESHOLDS,
  DISENGAGEMENT_OUTREACH_COOLDOWN_DAYS,
  parseThresholds,
  isDisengagementEligible,
  weekdaysSince,
  matchesMissedCheckIn,
  matchesMissedWorkout,
  matchesChatSilence,
  matchesChatSilenceSegment,
  evaluateDisengagement,
  isClientDisengaged,
  shouldSkipForCancellation,
};
